import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Sparkles, ShieldCheck, BookOpen, MessageCircle, Search, TrendingUp, BookMarked } from "lucide-react";
import { frameworks, glossary as glossaryApi, adminSeo } from "@/lib/api";
import usePageTitle from "@/lib/usePageTitle";
import PaywallModal from "@/components/PaywallModal";
import usePaywall from "@/lib/usePaywall";
import AiCitedBadge from "@/components/AiCitedBadge";
import NewsletterSignup from "@/components/NewsletterSignup";
import PersonaZone from "@/components/PersonaZone";
import SignalOfTheWeek from "@/components/SignalOfTheWeek";

const TEASER_FRAMEWORKS = [
  "ISO 31000", "COSO ERM", "NIST RMF", "ISO 27001", "Basel III/IV",
  "Solvency II", "FERMA", "COBIT", "PMBOK", "PRINCE2", "SAFe",
  "Six Sigma", "FMEA", "HAZOP", "Bow-Tie", "Monte Carlo", "FTA",
  "RCA", "SWOT/PESTLE", "ISO 9001",
];

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/45027623-19c4-462f-afcc-94d739d84810/images/f71a2884b2899cc3531d2af66963d1a46206be302532d945589a356a190d49e0.png";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1601881403737-2a6085443ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBuaWdodHxlbnwwfHx8fDE3Nzg4ODMzODV8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK_DESC = "India's most complete BFSI risk management knowledge platform. Deep-dive compliance guides for RBI MD-ITGRC, SEBI CSCRF, DPDP Act 2023, CERT-In Directions and IRDAI Guidelines. Free tools, calculators, templates and an AI-powered framework advisor.";

function buildDynamicDescription(stats) {
  if (!stats) return FALLBACK_DESC;
  const { frameworks: f, tools: t, glossary_terms: g, india_regulators: r } = stats;
  return `India's most complete BFSI risk management knowledge platform — ${f}+ risk frameworks (ISO 31000, COSO ERM, NIST RMF, Basel III/IV, DORA, NIS2 and more), ${r}+ India regulator deep-dives (RBI MD-ITGRC, SEBI CSCRF, IRDAI, CERT-In, DPDP Act 2023), ${t}+ interactive tools and ${g}+ glossary terms. AI-powered framework advisor for BFSI risk professionals.`;
}

export default function Landing() {
  const [count, setCount] = useState(20);
  const [previewTerms, setPreviewTerms] = useState([]);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [seoStats, setSeoStats] = useState(null);
  const paywall = usePaywall({ enableScroll: true, enableTime: true });

  usePageTitle(null, buildDynamicDescription(seoStats));

  useEffect(() => {
    frameworks
      .list()
      .then((fws) => setCount(fws.length))
      .catch(() => {});
    glossaryApi
      .preview(9)
      .then(setPreviewTerms)
      .catch(() => {});
    adminSeo
      .stats()
      .then(setSeoStats)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(() => {
      glossaryApi
        .list({ q, limit: 6 })
        .then((d) => setSearchResults(d.items || []))
        .catch(() => setSearchResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const onPreviewDetail = (e) => {
    if (paywall.registerDetailClick()) e.preventDefault();
  };

  return (
    <div className="min-h-screen bg-rp-bg text-rp-text">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden border-b border-rp-border">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-rp-bg via-rp-bg/95 to-rp-bg/30" />
        <div className="absolute inset-0 -z-10 rp-noise opacity-40" />

        <div className="rp-container py-28 md:py-40 max-w-5xl">
          <div className="rp-overline mb-6 animate-fade-up">AI · Risk Management · Encyclopedia</div>
          <h1
            className="font-display text-5xl md:text-7xl font-extrabold tracking-tight leading-[0.95] text-rp-text rp-text-shadow animate-fade-up"
            data-testid="hero-headline"
          >
            Match the right risk framework.
            <br />
            <span className="text-rp-accent">In under two minutes.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-rp-textDim leading-relaxed animate-fade-up">
            RiskPedia is the encyclopedia and AI advisor for risk professionals. Answer five questions about your
            industry, maturity and goals — get a tailored shortlist of frameworks with a Claude-powered chat
            advisor on standby.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3 animate-fade-up">
            <Link to="/signup" data-testid="hero-cta-signup">
              <Button className="bg-rp-accent hover:bg-rp-accentHover text-white font-medium px-6 py-6 text-base rounded-lg active:scale-95 transition">
                Get started — it&apos;s free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/library" data-testid="hero-cta-library">
              <Button
                variant="outline"
                className="border-rp-border bg-transparent text-rp-text hover:bg-rp-surface2 hover:border-rp-borderHover px-6 py-6 text-base rounded-lg"
              >
                Browse the library
              </Button>
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl" data-testid="hero-stat-strip">
            {[
              { value: seoStats?.frameworks ?? count, suffix: "+", label: "Risk frameworks" },
              { value: seoStats?.india_regulators ?? "39", suffix: "+", label: "India regulator hubs" },
              { value: seoStats?.tools ?? "87", suffix: "+", label: "Interactive tools" },
              { value: seoStats?.glossary_terms ?? "212", suffix: "+", label: "Glossary terms" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-lg border border-rp-border bg-rp-surface/40 px-4 py-3 hover:border-orange-400/40 transition-colors"
                data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-display text-2xl md:text-3xl font-extrabold text-rp-text leading-none">
                  {s.value}<span className="text-orange-400">{s.suffix}</span>
                </div>
                <div className="mt-1.5 text-[11px] font-mono uppercase tracking-[0.12em] text-rp-textDim">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <AiCitedBadge variant="compact" />
          </div>
        </div>
      </section>

      {/* SIGNAL OF THE WEEK — drives organic traffic to /money-flow/signals */}
      <div className="py-8 border-b border-rp-border bg-rp-surface/20">
        <SignalOfTheWeek />
      </div>

      {/* MARQUEE */}
      <section className="border-b border-rp-border overflow-hidden bg-rp-surface/30">
        <div className="rp-overline rp-container pt-10 pb-2">A curated index</div>
        <div className="relative py-10">
          <div className="flex animate-marquee gap-12 whitespace-nowrap will-change-transform">
            {[...TEASER_FRAMEWORKS, ...TEASER_FRAMEWORKS].map((name, i) => (
              <span
                key={`${name}-${i < TEASER_FRAMEWORKS.length ? "a" : "b"}`}
                className="font-display text-3xl md:text-4xl font-bold tracking-tight text-rp-textFaint hover:text-rp-text transition-colors"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
        <div className="rp-container pb-6 -mt-2">
          <Link to="/suggest" className="text-sm text-rp-textDim hover:text-rp-text inline-flex items-center gap-1" data-testid="landing-suggest-link">
            Don&apos;t see what you need? <span className="underline underline-offset-2">Suggest a framework →</span>
          </Link>
        </div>
      </section>

      {/* Newsletter signup — Phase B2 (homepage location) */}
      <section className="border-b border-rp-border">
        <div className="rp-container py-10">
          <NewsletterSignup source="homepage" />
        </div>
      </section>

      {/* India Regulatory Hub — Sprint 5C promo block */}
      <section className="border-b border-rp-border bg-gradient-to-r from-orange-500/[0.06] via-rp-bg to-emerald-500/[0.05]" data-testid="landing-india-hub-promo">
        <div className="rp-container py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/40 bg-orange-400/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-orange-300">
                <Sparkles className="h-3 w-3" /> New · India Regulatory Hub
              </div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                India&apos;s most complete <span className="text-orange-400">compliance deep-dive</span> for BFSI
              </h2>
              <p className="mt-4 text-rp-textDim max-w-2xl text-base">
                Deep-dive compliance guides for <strong className="text-rp-text">RBI, SEBI, IRDAI, CERT-In, DPDP and IBA</strong> — control catalogues, applicability matrices, compliance calendars, penalty frameworks, and free templates for Indian BFSI professionals.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-[11px] font-mono text-rp-textFaint">
                <span className="rounded-full border border-rp-border px-3 py-1">6 regulators</span>
                <span className="rounded-full border border-rp-border px-3 py-1">26+ CERT-In incident types</span>
                <span className="rounded-full border border-rp-border px-3 py-1">12 templates · 6 free</span>
                <span className="rounded-full border border-emerald-400/40 text-emerald-300 px-3 py-1">IRDAI Apr 2026 updated</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/india" data-testid="landing-india-cta">
                  <Button className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                    Explore India Hub <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/india/breach-playbook" className="inline-flex items-center gap-1.5 rounded-full border border-rp-border px-4 py-2 text-sm font-semibold text-rp-text hover:border-orange-400 transition-colors" data-testid="landing-india-breach-playbook-link">
                  Try the 6-hour breach playbook <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                { tag: "RBI", line: "MD-ITGRC 22 control domains", color: "emerald" },
                { tag: "SEBI", line: "CSCRF 5-category framework", color: "sky" },
                { tag: "IRDAI", line: "April 2026 — 14 security domains", color: "violet" },
                { tag: "CERT-In", line: "26 reportable incident types", color: "orange" },
                { tag: "DPDP", line: "8 fiduciary obligations + Rules 2025", color: "rose" },
                { tag: "IBA", line: "Model cyber policy + KYC/AML", color: "slate" },
              ].map((r) => (
                <Link key={r.tag} to={`/india/${r.tag.toLowerCase()}`} className="rounded-xl border border-rp-border bg-rp-surface p-3 hover:border-orange-400 transition-colors" data-testid={`landing-india-card-${r.tag.toLowerCase()}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-rp-textFaint">{r.tag}</div>
                  <div className="mt-1 text-rp-text text-sm font-semibold leading-tight">{r.line}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      <PersonaZone />

      {/* #16-deeper — Learn faster: Webinars + Shorts entry-point cards */}
      <section className="border-b border-rp-border" data-testid="landing-learn-faster">
        <div className="rp-container py-16">
          <div className="rp-overline mb-3">Learn faster</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">
            Watch the <span className="text-orange-300">60-second</span> and the
            <span className="text-red-300"> 45-minute</span> version.
          </h2>
          <p className="mt-3 max-w-2xl text-rp-textDim">
            Short on time? Pick the format that fits your day. Free to watch live; recordings included with Pro.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Link
              to="/webinars"
              className="group rounded-2xl border border-orange-400/40 bg-orange-500/[0.04] p-6 hover:border-orange-400 transition-colors"
              data-testid="landing-webinars-cta"
            >
              <div className="flex items-center gap-2 text-orange-300 mb-3">
                <span className="rounded-full border border-orange-400/40 bg-orange-400/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em]">India Risk Desk · monthly · 45 min</span>
              </div>
              <h3 className="font-display text-2xl font-extrabold">Live monthly webinars</h3>
              <p className="mt-2 text-sm text-rp-textDim">
                Websites Admin hosts a CRO, regulator or GRC consultant every month. Get the .ics invite, attend live, ask questions. Recordings for Pro members.
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-300 group-hover:gap-2.5 transition-all">
                See upcoming sessions <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
            <Link
              to="/youtube"
              className="group rounded-2xl border border-red-400/40 bg-red-500/[0.04] p-6 hover:border-red-400 transition-colors"
              data-testid="landing-youtube-cta"
            >
              <div className="flex items-center gap-2 text-red-300 mb-3">
                <span className="rounded-full border border-red-400/40 bg-red-400/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em]">RiskPedia Shorts · 60 seconds</span>
              </div>
              <h3 className="font-display text-2xl font-extrabold">DPDP, RBI, CERT-In — in a minute</h3>
              <p className="mt-2 text-sm text-rp-textDim">
                Bite-sized explainers on every India BFSI obligation. Watch on the train. Five active series. New Shorts weekly.
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-red-300 group-hover:gap-2.5 transition-all">
                Browse the library <ArrowRight className="h-4 w-4" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-rp-border" data-testid="landing-glossary">
        <div className="rp-container py-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="rp-overline mb-3 flex items-center gap-2">
                <TrendingUp className="h-3 w-3" /> Live · updated from trending searches
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                The Risk <span className="text-rp-accent">Glossary</span>
              </h2>
              <p className="mt-3 max-w-xl text-rp-textDim">
                200+ AI-curated definitions across ERM, cyber, operational, financial, ESG and emerging risk.
                Search a term to see a preview — open the full breakdown with a free account.
              </p>
            </div>
            <Link to="/glossary" data-testid="landing-glossary-all">
              <Button
                variant="outline"
                className="border-rp-border bg-transparent text-rp-text hover:bg-rp-surface2"
              >
                Browse all terms <ArrowRight className="ml-2 h-3 w-3" />
              </Button>
            </Link>
          </div>

          {/* search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rp-textFaint" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 'inherent risk', 'DORA', 'third-party'…"
              className="pl-10 bg-rp-surface border-rp-border text-rp-text focus:border-rp-accent h-12"
              data-testid="landing-glossary-search"
            />
          </div>

          {/* search results inline preview */}
          {searchResults.length > 0 && (
            <div className="mt-6 rounded-xl border border-rp-border bg-rp-surface divide-y divide-rp-border max-w-xl" data-testid="landing-search-results">
              {searchResults.map((t) => (
                <Link
                  key={t.slug}
                  to={`/glossary/${t.slug}`}
                  onClick={onPreviewDetail}
                  className="flex items-start gap-3 p-4 hover:bg-rp-surface2 transition-colors"
                  data-testid={`landing-result-${t.slug}`}
                >
                  <BookMarked className="h-4 w-4 mt-0.5 text-rp-accent shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-rp-text">{t.term}</div>
                    <div className="text-xs text-rp-textDim line-clamp-2 mt-1">{t.short_def}</div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-rp-textFaint mt-1.5" />
                </Link>
              ))}
            </div>
          )}

          {/* preview cards */}
          {query.trim().length < 2 && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="landing-glossary-preview">
              {previewTerms.map((t) => (
                <article
                  key={t.slug}
                  className="rp-glow-card rounded-xl border border-rp-border bg-rp-surface p-5 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-base font-bold leading-tight">{t.term}</h3>
                    <BookMarked className="h-4 w-4 text-rp-textFaint shrink-0" />
                  </div>
                  {t.category && (
                    <span className="mt-2 inline-block w-fit rounded-full bg-rp-accent/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] text-rp-accent">
                      {t.category}
                    </span>
                  )}
                  <p className="mt-2 text-xs text-rp-textDim line-clamp-3 flex-1">{t.short_def}</p>
                  <Link
                    to={`/glossary/${t.slug}`}
                    onClick={onPreviewDetail}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-rp-accent hover:underline"
                    data-testid={`landing-preview-${t.slug}`}
                  >
                    Detail <ArrowRight className="h-3 w-3" />
                  </Link>
                </article>
              ))}
              {previewTerms.length === 0 && (
                <div className="md:col-span-3 text-sm text-rp-textDim">Glossary is being seeded — check back in a moment.</div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS — bento */}
      <section className="rp-container py-28">
        <div className="rp-overline mb-4">How it works</div>
        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
          A five-question quiz, an AI shortlist, then a sit-down with the advisor.
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {[
            {
              num: "01",
              title: "Tell us about your context",
              body: "Industry, risk types, organisation size, current maturity, and primary goal. Five clicks.",
              icon: <Sparkles className="h-5 w-5" />,
              span: "md:col-span-7",
            },
            {
              num: "02",
              title: "AI ranks your matches",
              body: "Claude Sonnet 4.5 evaluates 20+ frameworks against your profile and returns the top 4 with match scores.",
              icon: <ShieldCheck className="h-5 w-5" />,
              span: "md:col-span-5",
            },
            {
              num: "03",
              title: "Browse the encyclopedia",
              body: "Open detail pages for ISO 31000, COSO ERM, NIST RMF and the rest — complexity, certification, time-to-implement.",
              icon: <BookOpen className="h-5 w-5" />,
              span: "md:col-span-5",
            },
            {
              num: "04",
              title: "Ask the AI advisor",
              body: "Compare frameworks, plan certifications, or ask 'where do I start?' — context-aware answers every time.",
              icon: <MessageCircle className="h-5 w-5" />,
              span: "md:col-span-7",
            },
          ].map((item) => (
            <div
              key={item.num}
              className={`${item.span} group rp-glow-card relative overflow-hidden rounded-xl border border-rp-border bg-rp-surface p-8`}
            >
              <div className="flex items-start justify-between">
                <span className="font-display text-5xl font-extrabold text-rp-textFaint group-hover:text-rp-accent transition-colors">
                  {item.num}
                </span>
                <span className="rounded-full border border-rp-border bg-rp-surface2 p-2 text-rp-accent">
                  {item.icon}
                </span>
              </div>
              <h3 className="font-display text-xl font-bold mt-8">{item.title}</h3>
              <p className="mt-3 text-rp-textDim leading-relaxed text-sm">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT split */}
      <section className="border-y border-rp-border bg-rp-surface/30">
        <div className="rp-container py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div
            className="aspect-[4/5] rounded-xl border border-rp-border bg-cover bg-center"
            style={{ backgroundImage: `url(${ABOUT_IMG})` }}
            aria-hidden
          />
          <div>
            <div className="rp-overline mb-4">Built for risk teams</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              Stop guessing which framework to adopt.
            </h2>
            <p className="mt-6 text-rp-textDim leading-relaxed">
              The risk world is fragmented — ISO 31000, COSO ERM, NIST RMF, Basel, Solvency II, PMBOK, COBIT,
              SAFe, Six Sigma, HAZOP, FMEA, Monte Carlo. Picking the right one for your context is its own
              risk. RiskPedia removes the guesswork with a structured, AI-assisted match.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-rp-text">
              {[
                "Personalised match scores for your profile",
                "Side-by-side framework comparison",
                "Save assessments, revisit them anytime",
                "Built-in AI advisor for follow-up questions",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rp-accent shadow-[0_0_8px_#4F7CFF]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link to="/signup" data-testid="about-cta">
                <Button className="bg-rp-accent hover:bg-rp-accentHover text-white font-medium px-6 py-5 rounded-lg">
                  Create your account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="rp-container py-24 text-center">
        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
          Find your framework. <span className="text-rp-accent">Today.</span>
        </h2>
        <p className="mt-4 text-rp-textDim">Sign up with email or Google. No credit card.</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/signup" data-testid="footer-cta-signup">
            <Button className="bg-rp-accent hover:bg-rp-accentHover text-white px-6 py-5 rounded-lg">
              Get started
            </Button>
          </Link>
          <Link to="/login" data-testid="footer-cta-login">
            <Button
              variant="outline"
              className="border-rp-border bg-transparent text-rp-text hover:bg-rp-surface2 hover:border-rp-borderHover px-6 py-5 rounded-lg"
            >
              Log in
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-rp-border bg-rp-surface/30" data-testid="site-footer">
        <div className="rp-container py-12 grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-rp-accent" />
              <span className="font-display font-bold text-rp-text">RiskPedia</span>
            </div>
            <p className="mt-2 text-xs text-rp-textFaint">The India BFSI risk framework encyclopedia and AI advisor.</p>
          </div>
          <div>
            <div className="rp-overline text-rp-textDim">Product</div>
            <ul className="mt-3 space-y-1.5 text-rp-textDim">
              <li><Link to="/library" className="hover:text-orange-300">Library</Link></li>
              <li><Link to="/india" className="hover:text-orange-300">India Hub</Link></li>
              <li><Link to="/india/alerts" className="hover:text-orange-300" data-testid="footer-india-alerts">Regulator Alerts</Link></li>
              <li><Link to="/tools" className="hover:text-orange-300">Tools</Link></li>
              <li><Link to="/webinars" className="hover:text-orange-300" data-testid="footer-webinars">Webinars</Link></li>
              <li><Link to="/youtube" className="hover:text-orange-300" data-testid="footer-youtube">YouTube Shorts</Link></li>
              <li><Link to="/report" className="hover:text-orange-300">Annual Report</Link></li>
              <li><Link to="/pro" className="hover:text-orange-300">Pro</Link></li>
            </ul>
          </div>
          <div>
            <div className="rp-overline text-rp-textDim">For business</div>
            <ul className="mt-3 space-y-1.5 text-rp-textDim">
              <li><Link to="/enterprise" className="hover:text-orange-300" data-testid="footer-enterprise">Enterprise</Link></li>
              <li><Link to="/consult" className="hover:text-orange-300">Consulting</Link></li>
              <li><Link to="/store" className="hover:text-orange-300">Template Store</Link></li>
              <li><Link to="/certify" className="hover:text-orange-300">Certify</Link></li>
              <li><Link to="/dashboard/api" className="hover:text-orange-300">API</Link></li>
            </ul>
          </div>
          <div>
            <div className="rp-overline text-rp-textDim">Partners</div>
            <ul className="mt-3 space-y-1.5 text-rp-textDim">
              <li><Link to="/partners" className="hover:text-orange-300">Partner with us</Link></li>
              <li><Link to="/advertise" className="hover:text-orange-300">Advertise</Link></li>
              <li><Link to="/sponsored" className="hover:text-orange-300">Sponsored content</Link></li>
              <li><Link to="/employers" className="hover:text-orange-300">Hire talent</Link></li>
              <li><Link to="/jobs" className="hover:text-orange-300">Jobs board</Link></li>
            </ul>
          </div>
          <div>
            <div className="rp-overline text-rp-textDim">Company</div>
            <ul className="mt-3 space-y-1.5 text-rp-textDim">
              <li><Link to="/press" className="hover:text-orange-300">Press</Link></li>
              <li><Link to="/guide" className="hover:text-orange-300">Site guide</Link></li>
              <li><Link to="/newsletter" className="hover:text-orange-300">Newsletter</Link></li>
              <li><Link to="/community" className="hover:text-orange-300">Community</Link></li>
              <li><a href="mailto:info@riskpedia.co.in" className="hover:text-orange-300">info@riskpedia.co.in</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-rp-border">
          <div className="rp-container py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-rp-textFaint">
            <span>© {new Date().getFullYear()} RiskPedia · The risk framework encyclopedia</span>
            <span>Informational only — verify against source circulars.</span>
          </div>
        </div>
      </footer>

      {paywall.triggered && (
        <PaywallModal reason={paywall.reason} onDismiss={paywall.dismiss} />
      )}
    </div>
  );
}
